/**
 * Shop the Look — interactive shoppable image.
 *
 * Desktop: floating hotspot cards pinned over a photo → click opens a panel
 * that grows out of the card to the side (FLIP animation).
 * Mobile:  horizontal scroll strip of landscape cards below the photo → tap
 * opens a bottom sheet with side‑by‑side layout.
 *
 * On mobile the very first panel is pre‑opened by default.
 * Tapping a different card swaps content in‑place — no close/reopen.
 * Desktop close behaviour is unchanged (X / Esc / click‑outside).
 *
 * Custom element: <shop-the-look>
 */

if (!customElements.get('shop-the-look')) {
  customElements.define(
    'shop-the-look',
    class ShopTheLook extends HTMLElement {
      connectedCallback() {
        this.desktopHotspots = Array.from(this.querySelectorAll('.shop-the-look__hotspot[data-stl-open]'));
        this.mobileHotspots  = Array.from(this.querySelectorAll('.shop-the-look__hotspot-mobile[data-stl-open]'));
        this.panels          = Array.from(this.querySelectorAll('[data-stl-panel]'));
        this.overlay         = this.querySelector('[data-stl-overlay]');
        this.desktopMq       = window.matchMedia('(min-width: 750px)');
        this.activeId        = null;

        // On mobile, show the first panel by default (no animation)
        if (!this.desktopMq.matches && this.panels.length > 0) {
          const firstId = this.panels[0].getAttribute('data-stl-panel');
          this._showPanelOnly(firstId);
        }

        // Bind listeners to all hotspot buttons (desktop + mobile)
        const allBtns = [...this.desktopHotspots, ...this.mobileHotspots];
        allBtns.forEach((btn) => {
          btn.addEventListener('click', () => {
            const id = btn.getAttribute('data-stl-open');
            // Mobile: always swap (never close); Desktop: toggle.
            if (!this.desktopMq.matches) {
              this.swap(id);
            } else {
              if (this.activeId === id) {
                this.close();
              } else {
                this.open(id);
              }
            }
          });
        });

        this.panels.forEach((panel) => {
          const closeBtn = panel.querySelector('[data-stl-close]');
          if (closeBtn) closeBtn.addEventListener('click', () => this.close());

          panel.querySelectorAll('[data-stl-variant]').forEach((chip) => {
            chip.addEventListener('click', () => this.selectVariant(panel, chip));
          });
        });

        // Scrim tap → close (desktop only)
        if (this.overlay) {
          this.overlay.addEventListener('click', () => {
            if (this.desktopMq.matches) this.close();
          });
        }

        this._onDocClick = (event) => {
          if (!this.activeId) return;
          if (event.target.closest('[data-stl-panel]')) return;
          if (event.target.closest('[data-stl-open]')) return;
          // Only close on desktop
          if (this.desktopMq.matches) this.close();
        };
        document.addEventListener('click', this._onDocClick);

        this._onKey = (event) => {
          if (event.key === 'Escape' && this.activeId) this.close();
        };
        document.addEventListener('keydown', this._onKey);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('keydown', this._onKey);
      }

      /* ---- Mobile: swap without close/reopen -------------------------------- */
      swap(id) {
        const panel = this.panels.find((p) => p.getAttribute('data-stl-panel') === id);
        if (!panel || id === this.activeId) return;

        // Hide the previously active panel instantly
        const prev = this.panels.find((p) => p.getAttribute('data-stl-panel') === this.activeId);
        if (prev) {
          prev.classList.remove('is-open');
          prev.hidden = true;
          this._resetStyles(prev);
        }

        // Show the new panel instantly (no slide animation)
        this.panels.forEach((p) => {
          if (p === panel) return;
          p.classList.remove('is-open');
          p.hidden = true;
          this._resetStyles(p);
        });
        panel.hidden = false;
        this._resetStyles(panel);
        panel.classList.add('is-open');

        // Update active state on mobile hotspot buttons
        this.mobileHotspots.forEach((btn) => {
          const active = btn.getAttribute('data-stl-open') === id;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-expanded', active ? 'true' : 'false');
        });

        this.activeId = id;
        this.classList.add('stl--open');
      }

      /* ---- Desktop: open with FLIP animation --------------------------------- */
      open(id) {
        const panel = this.panels.find((p) => p.getAttribute('data-stl-panel') === id);
        if (!panel) return;

        const hotspot =
          this.desktopHotspots.find((h) => h.getAttribute('data-stl-open') === id) ||
          this.mobileHotspots.find((h)  => h.getAttribute('data-stl-open') === id);

        this.panels.forEach((p) => {
          if (p === panel) return;
          this._cancelAnim(p);
          p.classList.remove('is-open');
          p.hidden = true;
          this._resetStyles(p);
        });

        panel.hidden = false;
        this._cancelAnim(panel);
        this._resetStyles(panel);

        if (this.desktopMq.matches && hotspot) {
          this._growFromCard(hotspot, panel);
        } else {
          void panel.offsetWidth;
          panel.classList.add('is-open');
        }

        this.desktopHotspots.forEach((btn) => {
          const active = btn.getAttribute('data-stl-open') === id;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-expanded', active ? 'true' : 'false');
        });
        this.mobileHotspots.forEach((btn) => {
          const active = btn.getAttribute('data-stl-open') === id;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-expanded', active ? 'true' : 'false');
        });

        this.classList.add('stl--open');
        this.activeId = id;
      }

      close() {
        const id = this.activeId;
        const panel = id ? this.panels.find((p) => p.getAttribute('data-stl-panel') === id) : null;
        const hotspot = id ? this.desktopHotspots.find((h) => h.getAttribute('data-stl-open') === id) : null;
        // Only the desktop panel retracts into its card; mobile just resets.
        const animate = this.desktopMq.matches && panel && hotspot && !panel.hidden;

        let cardRect = null;
        if (animate) {
          // Measure the card at full size WITHOUT revealing it: drop is-active
          // with transitions off, read the rect, restore it — all in one frame
          // so the card never paints. It stays hidden during the whole retract,
          // otherwise a big panel and a small card would show at the same time.
          hotspot.style.transition = 'none';
          hotspot.classList.remove('is-active');
          cardRect = hotspot.getBoundingClientRect();
          hotspot.classList.add('is-active');
          void hotspot.offsetWidth;
          hotspot.style.transition = '';
        }

        this.desktopHotspots.forEach((btn) => {
          btn.setAttribute('aria-expanded', 'false');
          // Keep the closing card hidden until the panel lands on it.
          if (animate && btn === hotspot) return;
          btn.classList.remove('is-active');
        });
        this.mobileHotspots.forEach((btn) => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-expanded', 'false');
        });
        this.classList.remove('stl--open');
        this.activeId = null;

        const finalize = () => {
          this.panels.forEach((p) => {
            this._cancelAnim(p);
            p.classList.remove('is-open');
            p.hidden = true;
            this._resetStyles(p);
          });
          if (hotspot) {
            // Panel is gone — reveal the card instantly so it swaps in exactly
            // where the panel landed, with no gap and no overlap.
            hotspot.style.transition = 'none';
            hotspot.classList.remove('is-active');
            void hotspot.offsetWidth;
            hotspot.style.transition = '';
          }
        };

        if (animate) {
          // Stop any pending animations on the other panels, then retract.
          this.panels.forEach((p) => { if (p !== panel) this._cancelAnim(p); });
          this._shrinkToCard(cardRect, panel, finalize);
        } else {
          finalize();
        }
      }

      /* ---- Internal: show a panel instantly, no animation -------------------- */
      _showPanelOnly(id) {
        const panel = this.panels.find((p) => p.getAttribute('data-stl-panel') === id);
        if (!panel) return;
        this.panels.forEach((p) => {
          if (p === panel) return;
          p.hidden = true;
        });
        panel.hidden = false;
        panel.classList.add('is-open');
        this.mobileHotspots.forEach((btn) => {
          const active = btn.getAttribute('data-stl-open') === id;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-expanded', active ? 'true' : 'false');
        });
        this.classList.add('stl--open');
        this.activeId = id;
      }

      /* ---- FLIP animation: grow out of the card with a spring (回弹) ---------- */
      _growFromCard(hotspot, panel) {
        this._cancelAnim(panel);
        const card = hotspot.getBoundingClientRect();
        panel.style.transition = 'none';
        panel.style.transform = 'none';
        panel.style.opacity = '0';
        panel.classList.add('is-open');
        const target = panel.getBoundingClientRect();
        if (!target.width || !target.height) { this._resetStyles(panel); return; }
        const sx = Math.max(card.width / target.width, 0.04);
        const sy = Math.max(card.height / target.height, 0.04);
        const dx = card.left + card.width / 2 - (target.left + target.width / 2);
        const dy = card.top + card.height / 2 - (target.top + target.height / 2);
        panel.style.transformOrigin = 'center center';
        panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
        void panel.offsetWidth;
        // Overshoot easing → panel pops out a touch past its resting size then
        // springs back (回弹).
        panel.style.transition = 'transform 0.5s cubic-bezier(0.34, 1.32, 0.56, 1), opacity 0.3s ease';
        panel.style.opacity = '1';
        panel.style.transform = 'none';
        const done = (event) => {
          if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
          this._cancelAnim(panel);
          this._resetStyles(panel);
        };
        panel._stlAnimEnd = done;
        panel.addEventListener('transitionend', done);
        panel._stlAnimTimer = setTimeout(done, 560);
      }

      /* ---- Reverse FLIP: retract the panel back into the card (回弹) --------- */
      _shrinkToCard(cardRect, panel, finalize) {
        this._cancelAnim(panel);
        const target = panel.getBoundingClientRect();
        if (!target.width || !target.height) { finalize(); return; }
        const sx = Math.max(cardRect.width / target.width, 0.04);
        const sy = Math.max(cardRect.height / target.height, 0.04);
        const dx = cardRect.left + cardRect.width / 2 - (target.left + target.width / 2);
        const dy = cardRect.top + cardRect.height / 2 - (target.top + target.height / 2);
        panel.style.transformOrigin = 'center center';
        panel.style.transition = 'none';
        panel.style.transform = 'none';
        panel.style.opacity = '1';
        void panel.offsetWidth;
        // No fade — the panel stays fully visible while it shrinks straight back
        // into its card, then is hidden once it lands.
        panel.style.transition = 'transform 0.36s cubic-bezier(0.5, 0, 0.75, 0.45)';
        panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
        const done = (event) => {
          if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
          this._cancelAnim(panel);
          finalize();
        };
        panel._stlAnimEnd = done;
        panel.addEventListener('transitionend', done);
        panel._stlAnimTimer = setTimeout(done, 480);
      }

      /* Cancel a panel's in-flight transition handler + safety timer. */
      _cancelAnim(panel) {
        if (panel._stlAnimEnd) {
          panel.removeEventListener('transitionend', panel._stlAnimEnd);
          panel._stlAnimEnd = null;
        }
        if (panel._stlAnimTimer) {
          clearTimeout(panel._stlAnimTimer);
          panel._stlAnimTimer = null;
        }
      }

      _resetStyles(panel) {
        panel.style.transition = '';
        panel.style.transform = '';
        panel.style.transformOrigin = '';
        panel.style.opacity = '';
      }

      /* ---- Variant switching ------------------------------------------------- */
      selectVariant(panel, chip) {
        panel.querySelectorAll('[data-stl-variant]').forEach((other) => {
          const on = other === chip;
          other.classList.toggle('is-active', on);
          other.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const price = chip.getAttribute('data-price');
        const priceEl = panel.querySelector('[data-stl-price]');
        if (price && priceEl) priceEl.textContent = price;
        const idInput = panel.querySelector('[data-stl-id]');
        if (idInput) idInput.value = chip.getAttribute('data-variant-id');
        const image = chip.getAttribute('data-image');
        const imageEl = panel.querySelector('[data-stl-panel-img]');
        if (image && imageEl && imageEl.tagName === 'IMG') imageEl.src = image;
      }
    }
  );
}
